import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Returns the total number of unread direct messages for the current user.
 * Lightweight: counts conversations where the latest message arrived after
 * the user's `last_read_at` and was sent by someone else.
 */
export const useUnreadMessages = () => {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }

    let cancelled = false;
    const compute = async () => {
      try {
        const { data: parts } = await supabase
          .from("conversation_participants")
          .select("conversation_id, last_read_at")
          .eq("user_id", user.id);
        const ids = (parts ?? []).map((p) => p.conversation_id);
        if (ids.length === 0) {
          if (!cancelled) setUnread(0);
          return;
        }
        const lastReadMap = new Map<string, string | null>();
        for (const p of parts ?? []) lastReadMap.set(p.conversation_id, p.last_read_at);

        const { data: msgs } = await supabase
          .from("messages")
          .select("conversation_id, sender_id, created_at")
          .in("conversation_id", ids)
          .order("created_at", { ascending: false })
          .limit(500);

        const seen = new Set<string>();
        let count = 0;
        for (const m of (msgs ?? []) as any[]) {
          if (seen.has(m.conversation_id)) continue;
          seen.add(m.conversation_id);
          if (m.sender_id === user.id) continue;
          const lr = lastReadMap.get(m.conversation_id);
          if (!lr || new Date(m.created_at) > new Date(lr)) count++;
        }
        if (!cancelled) setUnread(count);
      } catch {
        // ignore
      }
    };

    void compute();

    // Read receipts are scoped to this account. New-message counts refresh on
    // focus and on a quiet visible-page interval instead of listening to every
    // message created anywhere in Swiftix.
    const channel = supabase
      .channel(`unread-msgs:${user.id}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "conversation_participants", filter: `user_id=eq.${user.id}` }, () => {
        void compute();
      })
      .subscribe();

    const onLocalRead = () => { void compute(); };
    window.addEventListener("swiftix:messages-read", onLocalRead);
    const onVisible = () => { if (document.visibilityState === "visible") void compute(); };
    document.addEventListener("visibilitychange", onVisible);
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void compute();
    }, 60000);

    return () => {
      cancelled = true;
      window.removeEventListener("swiftix:messages-read", onLocalRead);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
      void supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return unread;
};
